import axios from 'axios';
import type { OptimizationJobCreateRequest } from '../types/optimizationJob';
import type {
  Leaderboard as LbLeaderboard,
  LeaderboardTable as LbTable,
  LeaderboardRun as LbRun,
  LeaderboardAlgorithm as LbAlgorithm,
  Submission as LbSubmission,
  MetaDataset as LbMetaDataset,
  MetaDatasetPayload as LbMetaDatasetPayload,
  LeaderboardProvider as LbProvider,
  CreateRunPayload as LbCreateRunPayload,
  Paged as LbPaged,
} from '../types/leaderboard';
import type {
  OptimizationPreviewData,
  OptimizationPreviewRequest,
} from '../types/optimizationPreview';
import { isLikelyTransientFetchError } from '../utils/errorUtils';
import { clampUsageLimit } from '../utils/usageContract';
import { unifiedWebSocketService } from './unifiedWebSocket';
import type { SingleAgentConfig } from './unifiedWebSocket';
import { useOptimizationStore } from '../store/optimizationStore';
import { API_BASE_URL, CONNECTOR_BASE_URL, VUSER_BASE_URL } from './config';
import { 
  ApiResponse, 
  AuthLinkRequestData, 
  AuthLinkCallbackData,
  AuthStatusData,
  ProfileData,
  OptimizationHistoryItem,
  OptimizationUsageData,
  IntegrationData,
  AdminUser,
  AdminUsageItem,
  AdminUsageByModelItem,
  UsageStats,
  UsageReconciliationResponse,
  AdminTopupRequest,
  AdminAssignRoleRequest,
  Paginated,
  UsageTransactionsResponse,
  UsageHistoryResponse,
  UsageDetailResponse,
  DatasetExportRequest,
  DatasetExportResponse,
  DatasetExportPreviewData,
  DatasetImportJob,
  DatasetContentResponse,
  DatasetSourceUsersData,
  DatasetSourceDialogsData,
} from '../types/api';
import type {
  ConnectorIntegrationConfigEnvelope,
  ConnectorIntegrationConfigResponse,
  ConnectorIntegrationListResponse,
  ConnectorIntegrationUsersResponse,
} from '../types/connector';
import type {
  WorkflowReadOptions,
  WorkflowReadResult,
  WorkflowRollbackRequest,
  WorkflowRollbackResult,
  WorkflowSaveRequest,
  WorkflowSaveResponse,
  WorkflowSnapshot,
} from '../types/workflow';
import type {
  Experiment,
  ExperimentScoreBreakdown,
  ExperimentTokenStatistics,
  Persona,
  Dialog,
  DialogTokenStatistics,
  Reaction,
  VirtualUser,
  ChatViewerLabelResponse,
  ChatViewerResolvedLabel,
  ChatViewerLabelUpsertBody,
  ChatViewerStepVirtualReaction,
} from '../types/vuser';
import type {
  RagDeleteFolderResponse,
  RagDeleteResponseData,
  RagDocument,
  RagFolder,
  RagPresignResponseData,
} from '../types/rag';
import type {
  NLTConfig,
  NLTPaginated,
  NLTPatchConfig,
  NLTPatchRun,
  NLTPatchVersion,
  SaveNLTConfigRequest,
  SavePatchConfigRequest,
  SavePatchConfigResponse,
  GeneratePatchRequest,
  NLTChatRequest,
  NLTChatResponse,
  NLTChatType,
  PatchDecisionRequest,
  PatchDecisionAcceptResponse,
  PatchDecisionRejectResponse,
} from '../types/nlt';
import type { NltWorkflowPatchRequest, NltWorkflowPatchResponse } from '../types/nltWorkflow';
import type {
  WorkflowEvaluationJob,
  WorkflowEvaluationJobList,
  WorkflowEvaluationJobRequest,
  WorkflowTuningChange,
  WorkflowTuningJob,
  WorkflowTuningJobList,
  WorkflowTuningJobRequest,
  WorkflowTuningJournal,
  WorkflowTuningPreview,
  WorkflowTuningProposal,
  WorkflowTuningUsage,
} from '../types/workflowTuning';

function createApiClient(baseURL: string) {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true, // Поддержка куков
  });

  // Глобальные перехватчики ответов API для авто-очистки при выходе
  client.interceptors.response.use(
    (response) => {
      try {
        const data = response?.data as any;
        if (
          data &&
          data.success === true &&
          data.data &&
          typeof data.data.message === 'string' &&
          data.data.message.toLowerCase() === 'signed out'
        ) {
          performGlobalSignoutCleanup();
        }
      } catch {}
      return response;
    },
    (error) => {
      // Любые 401 считаем потерей сессии — делаем безопасную очистку.
      // Кроме внутреннего контура коннектора: он авторизуется сервисным ключом, а не
      // кукой пользователя, поэтому его 401 говорит про ключ и ничего про сессию.
      // Без этой оговорки отклонённый ключ выкидывал бы пользователя из Тюнера.
      const url = String(error?.config?.url || '');
      const serviceAuthenticated = url.includes('/connector/internal/');
      if (error?.response?.status === 401 && !serviceAuthenticated) {
        performGlobalSignoutCleanup();
        // Синхронизируем React AuthContext (interceptors не знают о хуках)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('infiano:session-expired'));
        }
      }
      return Promise.reject(error);
    }
  );

  return client;
}

const api = createApiClient(API_BASE_URL);
const connectorApi = createApiClient(CONNECTOR_BASE_URL);

/**
 * Service key for the connector's internal contour. The fallback is a dev placeholder —
 * every caller must be gated on `workflowTestChatEnabled()` so it never reaches a build
 * that ships to qa or prod.
 */
function internalServiceKey(): string {
  return process.env.REACT_APP_CONNECTOR_SERVICE_TOKEN || 'internal-api-key';
}

/** Dev-only workflow test chat. Absent flag = the feature does not exist in that build. */
export function workflowTestChatEnabled(): boolean {
  return String(process.env.REACT_APP_WORKFLOW_TEST_CHAT || '').trim() === '1';
}
const vuserApi = createApiClient(VUSER_BASE_URL);

/**
 * Глобальная очистка состояния приложения при выходе пользователя.
 * Используется как при явном logout, так и при ответах API вида
 * { success: true, data: { message: "signed out" } } или статусе 401.
 */
export const performGlobalSignoutCleanup = () => {
  try {
    // Отключаем все активные сессии и чистим сохранённые run_id
    unifiedWebSocketService.disconnect(true);
  } catch (e) {
    console.warn('WS cleanup on signout failed:', e);
  }

  try {
    // Полная очистка zustand-хранилища от временных данных страниц
    const store = useOptimizationStore.getState();

    // Чаты
    store.clearSingleAgentChat();
    store.clearMultiAgentChat();

    // Single-agent оптимизация
    store.resetOptimization();
    store.setIsOptimized(false);
    store.setOptimizationProgress(0);

    // Графики/данные
    store.clearGraphData();

    // Multi-agent оптимизация
    store.setMultiAgentOptimizedPrompts([]);
    store.setMultiAgentIterationResults([]);
    store.setMultiAgentIsOptimized(false);
    store.setMultiAgentSelectedIterationIndex(0);
    store.setMultiAgentIsOptimizing(false);
    store.setMultiAgentOptimizationProgress(0);

    // Общие поля/вводы
    store.setInitialPrompt('');
    store.setBotPrompt('');
    store.setSelectedPrompt('');
    store.setSelectedPromptIndex('');

    // Сбрасываем выбор датасета, чтобы исключить зависшие состояния страниц
    store.setSelectedDataset('');

    // На уровне persist очищаем сохранённую копию
    // (на следующем монтировании дефолты подтянутся заново)
    // В некоторых окружениях persist может быть недоступен - проверяем опционально
    // @ts-ignore
    useOptimizationStore.persist?.clearStorage?.();
  } catch (e) {
    console.warn('Store cleanup on signout failed:', e);
  }
};

// Auth endpoints
export const auth = {
  signup: async (data: { email: string; password: string; name: string; company_name: string; ready_to_contact: boolean }): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/auth/signup', data);
    return response.data;
  },
  signin: async (data: { email: string; password: string }): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/auth/signin', data);
    return response.data;
  },
  verify: async (code: string): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/auth/verify?code=${code}`);
    return response.data;
  },
  signout: async (): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/auth/signout');
    return response.data;
  },
  checkStatus: async (): Promise<ApiResponse<AuthStatusData>> => {
    const response = await api.get<ApiResponse<AuthStatusData>>('/auth/status');
    return response.data;
  },
  // Новые методы для авторизации через email link
  requestAuthLink: async (email: string, captchaToken?: string): Promise<ApiResponse<AuthLinkRequestData>> => {
    const response = await api.post<ApiResponse<AuthLinkRequestData>>('/auth/auth-link/request', { 
      email,
      captcha_token: captchaToken || ''
    });
    return response.data;
  },
  callbackAuthLink: async (token: string): Promise<ApiResponse<AuthLinkCallbackData>> => {
    const response = await api.get<ApiResponse<AuthLinkCallbackData>>(`/auth/auth-link/callback?token=${token}`);
    return response.data;
  },
  getGoogleAuthURL: async (): Promise<ApiResponse<{ auth_url: string }>> => {
    const response = await api.get<ApiResponse<{ auth_url: string }>>('/auth/google/url');
    return response.data;
  },
  googleCallback: async (code: string, codeVerifier: string): Promise<ApiResponse<AuthLinkCallbackData>> => {
    const response = await api.post<ApiResponse<AuthLinkCallbackData>>('/auth/google/callback', {
      code,
      code_verifier: codeVerifier
    });
    return response.data;
  },
  
  // Новые методы для работы с профилем
  getProfile: async (): Promise<ApiResponse<ProfileData>> => {
    const response = await api.get<ApiResponse<ProfileData>>('/profile');
    return response.data;
  },
  
  updateProfile: async (data: { 
    name: string; 
    company_name: string; 
    ready_to_contact: boolean 
  }): Promise<ApiResponse<ProfileData>> => {
    const response = await api.put<ApiResponse<ProfileData>>('/profile', data);
    return response.data;
  },
};

// AutoTune endpoints
export const autoTune = {
  optimize: async (data: { 
    prompt: string; 
    dataset_name: string | string[]; 
    model_name?: string; 
    iterations?: number;
    temperature?: number;
  }) => {
    // Используем Unified WebSocket для оптимизации
    const config: SingleAgentConfig = {
      prompt: data.prompt,
      dataset_name: data.dataset_name,
      model_name: data.model_name,
      iterations: data.iterations,
      temperature: data.temperature
    };
    
    return new Promise((resolve, reject) => {
      unifiedWebSocketService.optimize(
        'single',
        config,
        {
          onComplete: (result) => resolve(result),
          onError: (error) => reject(error)
        }
      );
    });
  },
  
  optimizeWithProgress: async (
    data: { 
      prompt: string; 
      dataset_name: string | string[]; 
      model_name?: string; 
      iterations?: number;
      temperature?: number;
    },
    onProgress?: (progress: number) => void,
    onComplete?: (result: any) => void
  ) => {
    // Используем Unified WebSocket для оптимизации с прогрессом
    const config: SingleAgentConfig = {
      prompt: data.prompt,
      dataset_name: data.dataset_name,
      model_name: data.model_name,
      iterations: data.iterations,
      temperature: data.temperature
    };
    
    return new Promise((resolve, reject) => {
      unifiedWebSocketService.optimize(
        'single',
        config,
        {
          onProgress: onProgress,
          onComplete: (result) => {
          onComplete?.(result);
          resolve(result);
          },
          onError: (error) => reject(error)
        }
      );
    });
  },
  
  chat: async (data: { 
    temperature: number; 
    prompt: string; 
    dialog: Array<{ type: 'human' | 'ai'; content: string }> 
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post<ApiResponse>('/auto-tune/chat', data);
      return response.data;
    } catch (error) {
      console.error('Chat API error:', error);
      throw error;
    }
  },

  multiAgentChat: async (data: {
    model: {
      "master-agent": {
        name: string;
        prompt: string;
        model_name: string;
        temperature: number;
      };
      "worker-agents": Array<{
        name: string;
        prompt: string;
        model_name: string;
        temperature: number;
      }>;
    };
    dialog: Array<{ type: 'human' | 'ai'; content: string }>;
  }): Promise<ApiResponse> => {
    try {
      const response = await api.post<ApiResponse>('/auto-tune/multiagent/chat', data);
      return response.data;
    } catch (error) {
      console.error('Multi-agent chat API error:', error);
      throw error;
    }
  },
  
  getDefaults: async (): Promise<ApiResponse> => {
    const response = await api.get<ApiResponse>('/auto-tune/defaults');
    return response.data;
  },
  
  // История оптимизаций (GET /auto-tune/optimize/history?limit=&offset=)
  getOptimizationHistory: async (params?: {
    limit?: number;
    offset?: number;
    run_id?: string;
  }): Promise<ApiResponse<Paginated<OptimizationHistoryItem>>> => {
    try {
      const response = await api.get<ApiResponse<Paginated<OptimizationHistoryItem>>>(
        '/auto-tune/optimize/history',
        { params }
      );
      return response.data;
    } catch (error) {
      console.error('🌐 getOptimizationHistory API error:', error);
      throw error;
    }
  },
  
  getOptimizationUsage: async (runId: string): Promise<ApiResponse<OptimizationUsageData>> => {
    try {
      const response = await api.get<ApiResponse<OptimizationUsageData>>(`/auto-tune/optimize/usage/${runId}`);
      return response.data;
    } catch (error) {
      console.error('🌐 getOptimizationUsage API error:', error);
      throw error;
    }
  },
  
  changeSelectedPrompt: async (data: { 
    selected_prompt_id: number; 
    run_id: string 
  }): Promise<ApiResponse> => {
    const response = await api.put<ApiResponse>('/auto-tune/optimize/selected-prompt', data);
    return response.data;
  },
  
  saveToHistory: async (data: {
    run_id: string;
    initial_prompt: string;
    optimized_prompts: string[];
    model_name: string;
    dataset_name: string | string[];
    temperature: number;
    iterations: number;
    steps?: Array<{
      step_number: number;
      prompt: string;
      embedding_point?: { x: number; y: number };
      evaluation_point?: { x: number; y: number };
      timestamp: string;
      model_name: string;
      temperature: number;
    }>;
  }): Promise<ApiResponse> => {
    console.log('🌐 saveToHistory API call with data:', data);
    try {
      const response = await api.post<ApiResponse>('/auto-tune/optimize/history', data);
      console.log('🌐 saveToHistory API response:', response.data);
      return response.data;
    } catch (error) {
      console.error('🌐 saveToHistory API error:', error);
      throw error;
    }
  },
  
  exportStep: async (data: {
    run_id: string;
    step_number: number;
  }): Promise<ApiResponse<{
    success: boolean;
    message: string;
    data: {
      id: number;
      config: string; // JSON string
      label: string;
      user_id: string | null;
      created_at: string;
      updated_at: string;
    };
  }>> => {
    try {
      const response = await api.post<ApiResponse<{
        success: boolean;
        message: string;
        data: {
          id: number;
          config: string;
          label: string;
          user_id: string | null;
          created_at: string;
          updated_at: string;
        };
      }>>('/auto-tune/optimize/export-step', data);
      return response.data;
    } catch (error) {
      console.error('🌐 exportStep API error:', error);
      throw error;
    }
  },

  /** Preview optimization: estimate token usage for a full run (ML predict_token_usage). */
  optimizePreview: async (
    payload: OptimizationPreviewRequest
  ): Promise<ApiResponse<OptimizationPreviewData>> => {
    try {
      const response = await api.post<ApiResponse<OptimizationPreviewData>>(
        '/auto-tune/optimize/preview',
        payload
      );
      return response.data;
    } catch (error) {
      console.error('🌐 optimizePreview API error:', error);
      throw error;
    }
  },

  createOptimizationJob: async (body: OptimizationJobCreateRequest): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/auto-tune/optimize/jobs', body);
    return response.data;
  },

  getOptimizationJob: async (jobId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/auto-tune/optimize/jobs/${jobId}`);
    return response.data;
  },

  listOptimizationJobs: async (params?: { limit?: number; offset?: number }): Promise<ApiResponse<Paginated<any>>> => {
    const response = await api.get<ApiResponse<Paginated<any>>>('/auto-tune/optimize/jobs', { params });
    return response.data;
  },

  cancelOptimizationJob: async (jobId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/auto-tune/optimize/jobs/${jobId}/cancel`);
    return response.data;
  },

  /** Creates a new config tuning job from a failed/cancelled job; returns the new job payload. */
  retryOptimizationJob: async (jobId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      `/auto-tune/optimize/jobs/${encodeURIComponent(jobId)}/retry`
    );
    return response.data;
  },

  /** Preview evaluation job — estimate tokens/cost before starting (sync). */
  previewEvaluationJob: async (
    integrationId: string,
    body: import('../types/evaluation').EvaluationRunRequestBody
  ): Promise<ApiResponse<import('../types/evaluationJob').EvaluationJobPreviewData>> => {
    const response = await api.post<
      ApiResponse<import('../types/evaluationJob').EvaluationJobPreviewData>
    >('/auto-tune/evaluation/jobs/preview', body, {
      params: { integration_id: integrationId },
    });
    return response.data;
  },

  createEvaluationJob: async (
    integrationId: string,
    body: import('../types/evaluation').EvaluationRunRequestBody
  ): Promise<ApiResponse<import('../types/evaluationJob').EvaluationJob>> => {
    const response = await api.post<ApiResponse<import('../types/evaluationJob').EvaluationJob>>(
      '/auto-tune/evaluation/jobs',
      body,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  getEvaluationJob: async (
    jobId: string
  ): Promise<ApiResponse<import('../types/evaluationJob').EvaluationJob>> => {
    const response = await api.get<ApiResponse<import('../types/evaluationJob').EvaluationJob>>(
      `/auto-tune/evaluation/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  listEvaluationJobs: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<import('../types/evaluationJob').EvaluationJobListResponse>> => {
    const response = await api.get<
      ApiResponse<import('../types/evaluationJob').EvaluationJobListResponse>
    >('/auto-tune/evaluation/jobs', { params });
    return response.data;
  },

  cancelEvaluationJob: async (
    jobId: string
  ): Promise<ApiResponse<import('../types/evaluationJob').EvaluationJob>> => {
    const response = await api.post<ApiResponse<import('../types/evaluationJob').EvaluationJob>>(
      `/auto-tune/evaluation/jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return response.data;
  },

  retryEvaluationJob: async (
    jobId: string
  ): Promise<ApiResponse<import('../types/evaluationJob').EvaluationJob>> => {
    const response = await api.post<ApiResponse<import('../types/evaluationJob').EvaluationJob>>(
      `/auto-tune/evaluation/jobs/${encodeURIComponent(jobId)}/retry`
    );
    return response.data;
  },

  deleteEvaluationJob: async (
    jobId: string
  ): Promise<ApiResponse<{ id: string; deleted: boolean }>> => {
    const response = await api.delete<ApiResponse<{ id: string; deleted: boolean }>>(
      `/auto-tune/evaluation/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  // ── Workflow tuning (AIT-138) ─────────────────────────────────────────────
  //
  // Sibling namespace of `/auto-tune/optimize/*`, not a mode of it: these tune the
  // target system's state graph. `integration_id` travels in the body on both
  // branches here, unlike plain evaluation which takes it in the query.

  createWorkflowTuningJob: async (
    body: WorkflowTuningJobRequest
  ): Promise<ApiResponse<WorkflowTuningJob>> => {
    const response = await api.post<ApiResponse<WorkflowTuningJob>>('/auto-tune/workflow/jobs', body);
    return response.data;
  },

  /** Same body as `createWorkflowTuningJob` — the estimate is for the exact run that would start. */
  previewWorkflowTuningJob: async (
    body: WorkflowTuningJobRequest
  ): Promise<ApiResponse<WorkflowTuningPreview>> => {
    const response = await api.post<ApiResponse<WorkflowTuningPreview>>('/auto-tune/workflow/jobs/preview', body);
    return response.data;
  },

  listWorkflowTuningJobs: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WorkflowTuningJobList>> => {
    const response = await api.get<ApiResponse<WorkflowTuningJobList>>('/auto-tune/workflow/jobs', { params });
    return response.data;
  },

  getWorkflowTuningJob: async (jobId: string): Promise<ApiResponse<WorkflowTuningJob>> => {
    const response = await api.get<ApiResponse<WorkflowTuningJob>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  /** Only the changed paths — the diff UI is built from this, not from the full proposal. */
  getWorkflowTuningChanges: async (jobId: string): Promise<ApiResponse<WorkflowTuningChange[]>> => {
    const response = await api.get<ApiResponse<WorkflowTuningChange[]>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/changes`
    );
    return response.data;
  },

  /** The whole document, megabytes on a full graph — fetch only when applying. */
  getWorkflowTuningProposal: async (jobId: string): Promise<ApiResponse<WorkflowTuningProposal>> => {
    const response = await api.get<ApiResponse<WorkflowTuningProposal>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/proposal`
    );
    return response.data;
  },

  getWorkflowTuningJournal: async (jobId: string): Promise<ApiResponse<WorkflowTuningJournal>> => {
    const response = await api.get<ApiResponse<WorkflowTuningJournal>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/journal`
    );
    return response.data;
  },

  getWorkflowTuningUsage: async (jobId: string): Promise<ApiResponse<WorkflowTuningUsage>> => {
    const response = await api.get<ApiResponse<WorkflowTuningUsage>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/usage`
    );
    return response.data;
  },

  cancelWorkflowTuningJob: async (jobId: string): Promise<ApiResponse<WorkflowTuningJob>> => {
    const response = await api.post<ApiResponse<WorkflowTuningJob>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return response.data;
  },

  retryWorkflowTuningJob: async (jobId: string): Promise<ApiResponse<WorkflowTuningJob>> => {
    const response = await api.post<ApiResponse<WorkflowTuningJob>>(
      `/auto-tune/workflow/jobs/${encodeURIComponent(jobId)}/retry`
    );
    return response.data;
  },

  // Workflow evaluation — measures the graph as it stands, improves nothing.

  createWorkflowEvaluationJob: async (
    body: WorkflowEvaluationJobRequest
  ): Promise<ApiResponse<WorkflowEvaluationJob>> => {
    const response = await api.post<ApiResponse<WorkflowEvaluationJob>>(
      '/auto-tune/workflow/evaluation/jobs',
      body
    );
    return response.data;
  },

  listWorkflowEvaluationJobs: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<WorkflowEvaluationJobList>> => {
    const response = await api.get<ApiResponse<WorkflowEvaluationJobList>>(
      '/auto-tune/workflow/evaluation/jobs',
      { params }
    );
    return response.data;
  },

  getWorkflowEvaluationJob: async (jobId: string): Promise<ApiResponse<WorkflowEvaluationJob>> => {
    const response = await api.get<ApiResponse<WorkflowEvaluationJob>>(
      `/auto-tune/workflow/evaluation/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  cancelWorkflowEvaluationJob: async (jobId: string): Promise<ApiResponse<WorkflowEvaluationJob>> => {
    const response = await api.post<ApiResponse<WorkflowEvaluationJob>>(
      `/auto-tune/workflow/evaluation/jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return response.data;
  },

  retryWorkflowEvaluationJob: async (jobId: string): Promise<ApiResponse<WorkflowEvaluationJob>> => {
    const response = await api.post<ApiResponse<WorkflowEvaluationJob>>(
      `/auto-tune/workflow/evaluation/jobs/${encodeURIComponent(jobId)}/retry`
    );
    return response.data;
  },

  deleteWorkflowEvaluationJob: async (jobId: string): Promise<ApiResponse<{ id: string; deleted: boolean }>> => {
    const response = await api.delete<ApiResponse<{ id: string; deleted: boolean }>>(
      `/auto-tune/workflow/evaluation/jobs/${encodeURIComponent(jobId)}`
    );
    return response.data;
  },

  // Pipeline scheduling
  createPipeline: async (
    body: import('../types/pipeline').CreatePipelineRequest
  ): Promise<ApiResponse<import('../types/pipeline').Pipeline>> => {
    const response = await api.post<ApiResponse<import('../types/pipeline').Pipeline>>('/auto-tune/pipelines', body);
    return response.data;
  },

  listPipelines: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<import('../types/pipeline').PipelineListResponse>> => {
    const response = await api.get<ApiResponse<import('../types/pipeline').PipelineListResponse>>('/auto-tune/pipelines', {
      params,
    });
    return response.data;
  },

  getPipeline: async (pipelineId: string): Promise<ApiResponse<import('../types/pipeline').Pipeline>> => {
    const response = await api.get<ApiResponse<import('../types/pipeline').Pipeline>>(`/auto-tune/pipelines/${pipelineId}`);
    return response.data;
  },

  updatePipeline: async (
    pipelineId: string,
    body: import('../types/pipeline').UpdatePipelineRequest
  ): Promise<ApiResponse<import('../types/pipeline').Pipeline>> => {
    const response = await api.patch<ApiResponse<import('../types/pipeline').Pipeline>>(
      `/auto-tune/pipelines/${pipelineId}`,
      body
    );
    return response.data;
  },

  deletePipeline: async (
    pipelineId: string
  ): Promise<ApiResponse<import('../types/pipeline').DeletePipelineResponse>> => {
    const response = await api.delete<ApiResponse<import('../types/pipeline').DeletePipelineResponse>>(
      `/auto-tune/pipelines/${pipelineId}`
    );
    return response.data;
  },

  startPipelineRun: async (pipelineId: string): Promise<ApiResponse<import('../types/pipeline').PipelineRun>> => {
    const response = await api.post<ApiResponse<import('../types/pipeline').PipelineRun>>(
      `/auto-tune/pipelines/${pipelineId}/runs`
    );
    return response.data;
  },

  listPipelineRuns: async (
    pipelineId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<Paginated<import('../types/pipeline').PipelineRun>>> => {
    const response = await api.get<ApiResponse<Paginated<import('../types/pipeline').PipelineRun>>>(
      `/auto-tune/pipelines/${pipelineId}/runs`,
      { params }
    );
    return response.data;
  },

  getPipelineRun: async (runId: string): Promise<ApiResponse<import('../types/pipeline').PipelineRun>> => {
    const response = await api.get<ApiResponse<import('../types/pipeline').PipelineRun>>(
      `/auto-tune/pipeline-runs/${runId}`
    );
    return response.data;
  },

  cancelPipelineRun: async (runId: string): Promise<ApiResponse<import('../types/pipeline').PipelineRun>> => {
    const response = await api.post<ApiResponse<import('../types/pipeline').PipelineRun>>(
      `/auto-tune/pipeline-runs/${runId}/cancel`
    );
    return response.data;
  },
};

// ─── Лидерборд алгоритмов Prompt Tuning (эпик AIT-109) ──────────────────────
//
// Всё под /auto-tune/leaderboards в обычной оболочке {success, data}.
// Списки приходят как {items, pagination}.
export const leaderboard = {
  /** Список лидербордов — по одному на мета-датасет. */
  list: async (params?: { limit?: number; offset?: number }): Promise<ApiResponse<LbPaged<LbLeaderboard>>> => {
    const response = await api.get<ApiResponse<LbPaged<LbLeaderboard>>>('/auto-tune/leaderboards/', { params });
    return response.data;
  },

  /**
   * Таблица лидерборда. Строки уже отсортированы бэкендом по первичной метрике
   * с учётом направления — своя сортировка перевернёт её при `minimize`.
   * Пустая таблица (`rows: []`) — нормальный ответ, а не 404.
   */
  getTable: async (leaderboardId: string): Promise<ApiResponse<LbTable>> => {
    const response = await api.get<ApiResponse<LbTable>>(
      `/auto-tune/leaderboards/${encodeURIComponent(leaderboardId)}/table`
    );
    return response.data;
  },

  /**
   * История сабмишенов алгоритма, свежие первыми.
   * `algorithmId` — внутренний UUID из `row.algorithm.id`, не строка `legacy:...@...`.
   */
  listSubmissions: async (
    leaderboardId: string,
    algorithmId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<LbPaged<LbSubmission>>> => {
    const response = await api.get<ApiResponse<LbPaged<LbSubmission>>>(
      `/auto-tune/leaderboards/${encodeURIComponent(leaderboardId)}/algorithms/${encodeURIComponent(algorithmId)}/submissions`,
      { params }
    );
    return response.data;
  },

  // ── Мета-датасеты ─────────────────────────────────────────────────────────

  /** Лидерборд создаётся вместе с мета-датасетом, отдельного вызова не нужно. */
  createMetaDataset: async (payload: LbMetaDatasetPayload): Promise<ApiResponse<LbMetaDataset>> => {
    const response = await api.post<ApiResponse<LbMetaDataset>>('/auto-tune/leaderboards/meta-datasets', payload);
    return response.data;
  },

  listMetaDatasets: async (params?: { limit?: number; offset?: number }): Promise<ApiResponse<LbPaged<LbMetaDataset>>> => {
    const response = await api.get<ApiResponse<LbPaged<LbMetaDataset>>>('/auto-tune/leaderboards/meta-datasets', { params });
    return response.data;
  },

  getMetaDataset: async (id: string): Promise<ApiResponse<LbMetaDataset>> => {
    const response = await api.get<ApiResponse<LbMetaDataset>>(
      `/auto-tune/leaderboards/meta-datasets/${encodeURIComponent(id)}`
    );
    return response.data;
  },

  /** Состав присылается целиком, дельтой нельзя. */
  updateMetaDataset: async (id: string, payload: LbMetaDatasetPayload): Promise<ApiResponse<LbMetaDataset>> => {
    const response = await api.put<ApiResponse<LbMetaDataset>>(
      `/auto-tune/leaderboards/meta-datasets/${encodeURIComponent(id)}`,
      payload
    );
    return response.data;
  },

  /** Удаляет вместе с лидербордом и всей историей сабмишенов. */
  deleteMetaDataset: async (id: string): Promise<ApiResponse<unknown>> => {
    const response = await api.delete<ApiResponse<unknown>>(
      `/auto-tune/leaderboards/meta-datasets/${encodeURIComponent(id)}`
    );
    return response.data;
  },

  // ── Алгоритмы и прогоны ───────────────────────────────────────────────────

  /** Отключённые показываем отдельно и с `reason` — отправить их бэкенд не даст. */
  listAlgorithms: async (params?: { include_disabled?: boolean; limit?: number; offset?: number }): Promise<ApiResponse<LbPaged<LbAlgorithm>>> => {
    const response = await api.get<ApiResponse<LbPaged<LbAlgorithm>>>('/auto-tune/leaderboards/algorithms', { params });
    return response.data;
  },

  syncAlgorithms: async (): Promise<ApiResponse<unknown>> => {
    const response = await api.post<ApiResponse<unknown>>('/auto-tune/leaderboards/algorithms/sync');
    return response.data;
  },

  /**
   * Провайдеры и их модели для формы мета-датасета (FE-1). Не кэшируется на
   * бэкенде: список локального шлюза меняется, когда на него заливают модель.
   */
  listProviders: async (): Promise<ApiResponse<{ providers: LbProvider[] }>> => {
    const response = await api.get<ApiResponse<{ providers: LbProvider[] }>>('/auto-tune/leaderboards/providers');
    return response.data;
  },

  createRun: async (leaderboardId: string, payload: LbCreateRunPayload): Promise<ApiResponse<LbRun>> => {
    const response = await api.post<ApiResponse<LbRun>>(
      `/auto-tune/leaderboards/${encodeURIComponent(leaderboardId)}/runs`,
      payload
    );
    return response.data;
  },

  getRun: async (runId: string): Promise<ApiResponse<LbRun>> => {
    const response = await api.get<ApiResponse<LbRun>>(`/auto-tune/leaderboards/runs/${encodeURIComponent(runId)}`);
    return response.data;
  },

  cancelRun: async (runId: string): Promise<ApiResponse<LbRun>> => {
    const response = await api.post<ApiResponse<LbRun>>(
      `/auto-tune/leaderboards/runs/${encodeURIComponent(runId)}/cancel`
    );
    return response.data;
  },

  cancelSubmission: async (submissionId: string): Promise<ApiResponse<LbSubmission>> => {
    const response = await api.post<ApiResponse<LbSubmission>>(
      `/auto-tune/leaderboards/submissions/${encodeURIComponent(submissionId)}/cancel`
    );
    return response.data;
  },
};

// Integration endpoints
export const integration = {
  createOrUpdate: async (data: {
    username: string;
    friends_usernames: string[];
    init_config: {
      model_name: string;
      prompt: string;
      temperature: number;
    };
    opti_config: {
      model_name: string;
      prompt: string;
      temperature: number;
    };
  }): Promise<ApiResponse<IntegrationData>> => {
    try {
      console.log('🌐 Integration create/update API call with data:', data);
      const response = await api.post<ApiResponse<IntegrationData>>('/telegram/integration', data);
      console.log('🌐 Integration create/update API response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Integration create/update API error:', error);
      console.error('Error response data:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      throw error;
    }
  },

  get: async (): Promise<ApiResponse<IntegrationData>> => {
    try {
      const response = await api.get<ApiResponse<IntegrationData>>('/telegram/integration');
      return response.data;
    } catch (error) {
      console.error('Integration get API error:', error);
      throw error;
    }
  },
};

// Admin endpoints
export const admin = {
  getUsers: async (params?: {
    limit?: number; // 1-100, default: 20
    offset?: number; // >=0, default: 0
    q?: string; // search by email or name
  }): Promise<ApiResponse<Paginated<AdminUser>>> => {
    const response = await api.get<ApiResponse<Paginated<AdminUser>>>('/admin/users', { params });
    return response.data;
  },

  getUsage: async (params?: {
    from?: string; // RFC3339, exclusive
    to?: string; // RFC3339, inclusive
    limit?: number; // 1-100, default: 20
    offset?: number; // >=0, default: 0
  }): Promise<ApiResponse<Paginated<AdminUsageItem>>> => {
    const response = await api.get<ApiResponse<Paginated<AdminUsageItem>>>('/admin/usage', { params });
    return response.data;
  },

  getUsageByUser: async (userId: string): Promise<ApiResponse<AdminUsageItem>> => {
    const response = await api.get<ApiResponse<AdminUsageItem>>(`/admin/usage/${userId}`);
    return response.data;
  },

  topupUser: async (userId: string, data: AdminTopupRequest): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>(`/admin/users/${userId}/topup`, data);
    return response.data;
  },

  assignRole: async (data: AdminAssignRoleRequest): Promise<ApiResponse> => {
    const response = await api.post<ApiResponse>('/admin/users/assign-role', data);
    return response.data;
  },

  // High-level usage statistics for billing dashboard (legacy)
  getUsageStatistics: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<UsageTransactionsResponse>> => {
    const response = await api.get<ApiResponse<UsageTransactionsResponse>>('/admin/usage/transactions', { params });
    return response.data;
  },

  // New admin usage history
  getUsageHistory: async (params?: {
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
    user_id?: string;
    functionality?: string;
    status?: string;
    exclude_service_usage?: boolean;
  }): Promise<ApiResponse<UsageHistoryResponse>> => {
    const response = await api.get<ApiResponse<UsageHistoryResponse>>('/admin/usage/history', { params });
    return response.data;
  },

  getUsageDetail: async (functionality: string, entityId: string, params?: { user_id?: string }): Promise<ApiResponse<UsageDetailResponse>> => {
    const response = await api.get<ApiResponse<UsageDetailResponse>>(`/admin/usage/history/${functionality}/${entityId}`, { params });
    return response.data;
  },

  /**
   * AIT-258: разрез расхода по паре (пользователь, модель) за период.
   * Без `user_id` агрегирует по всем пользователям.
   */
  getUsageByModel: async (params?: {
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
    user_id?: string;
    exclude_service_usage?: boolean;
  }): Promise<ApiResponse<Paginated<AdminUsageByModelItem>>> => {
    const response = await api.get<ApiResponse<Paginated<AdminUsageByModelItem>>>('/admin/usage/by-model', {
      params: { ...params, limit: clampUsageLimit(params?.limit) },
    });
    return response.data;
  },

  /**
   * AIT-259: отчёт сверки ML-леджера с базой.
   * Ответ приходит в трёх разных формах — разбирается `parseReconciliation`.
   */
  getUsageReconciliation: async (): Promise<ApiResponse<UsageReconciliationResponse>> => {
    const response = await api.get<ApiResponse<UsageReconciliationResponse>>('/admin/usage/reconciliation');
    return response.data;
  },
};

// User usage endpoints
export const usage = {
  /** Суммы за период без разреза. Разбивка по модели есть только в админском /admin/usage/by-model. */
  getStats: async (params?: { from?: string; to?: string }): Promise<ApiResponse<UsageStats>> => {
    const response = await api.get<ApiResponse<UsageStats>>('/usage/stats', { params });
    return response.data;
  },

  getTransactions: async (params?: {
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
  }): Promise<ApiResponse<UsageTransactionsResponse>> => {
    const response = await api.get<ApiResponse<UsageTransactionsResponse>>('/usage/transactions', {
      params: params ? { ...params, limit: clampUsageLimit(params.limit) } : undefined,
    });
    return response.data;
  },

  // New usage history
  getHistory: async (params?: {
    limit?: number;
    offset?: number;
    from?: string;
    to?: string;
    functionality?: string;
    status?: string;
    exclude_service_usage?: boolean;
  }): Promise<ApiResponse<UsageHistoryResponse>> => {
    const response = await api.get<ApiResponse<UsageHistoryResponse>>('/usage/history', {
      params: params ? { ...params, limit: clampUsageLimit(params.limit) } : undefined,
    });
    return response.data;
  },

  getDetail: async (functionality: string, entityId: string): Promise<ApiResponse<UsageDetailResponse>> => {
    const response = await api.get<ApiResponse<UsageDetailResponse>>(`/usage/history/${functionality}/${entityId}`);
    return response.data;
  },
};

// Connector endpoints (new)
export const connector = {
  // Admin: list all integrations
  listIntegrationsAdmin: async (): Promise<ConnectorIntegrationListResponse> => {
    const response = await connectorApi.get<ConnectorIntegrationListResponse>('/connector/admin/integrations');
    return response.data;
  },

  // Admin: patch integration fields (e.g. url)
  patchIntegrationAdmin: async (integrationId: string, data: Record<string, any>): Promise<any> => {
    const response = await connectorApi.patch(`/connector/admin/integrations/${integrationId}`, data);
    return response.data;
  },

  // Admin: delete integration
  deleteIntegrationAdmin: async (integrationId: string): Promise<any> => {
    const response = await connectorApi.delete(`/connector/admin/integrations/${integrationId}`);
    return response.data;
  },

  // Admin: assign user to integration
  assignUserToIntegration: async (integrationId: string, user_id: string): Promise<any> => {
    const response = await connectorApi.post(`/connector/admin/integrations/${integrationId}/users`, { user_id });
    return response.data;
  },

  // Admin: list users in integration
  listUsersInIntegration: async (integrationId: string): Promise<ConnectorIntegrationUsersResponse> => {
    const response = await connectorApi.get<ConnectorIntegrationUsersResponse>(`/connector/admin/integrations/${integrationId}/users`);
    return response.data;
  },

  // User/control: get integration config
  getIntegrationConfig: async (integrationId: string): Promise<ConnectorIntegrationConfigResponse> => {
    const response = await connectorApi.get<ConnectorIntegrationConfigResponse>(
      `/connector/integrations/control/${integrationId}/config`,
      {
        // Prevent stale config reads when config was changed externally.
        params: { _t: Date.now() },
      }
    );
    return response.data;
  },

  // User/control: set integration config
  setIntegrationConfig: async (integrationId: string, body: ConnectorIntegrationConfigEnvelope): Promise<any> => {
    const response = await connectorApi.post(`/connector/integrations/control/${integrationId}/config`, body);
    return response.data;
  },

  // ─── Target-system workflow (docs/WORKFLOW_API.md) ──────────────────────
  // These three endpoints return their body as-is: there is NO { success, data }
  // envelope. Auth is the `access_token` cookie (already sent via withCredentials).

  /**
   * GET …/workflow — read the workflow snapshot (graph, versions, component
   * library, …). Pass the quoted ETag from a previous read to get a 304 with an
   * empty body when nothing changed. Deliberately NO `_t` cache-buster: freshness
   * comes from the digest, a unique URL would defeat the conditional GET and pull
   * ~1.5 MB on every render.
   */
  getWorkflow: async (integrationId: string, opts: WorkflowReadOptions = {}): Promise<WorkflowReadResult> => {
    const params: Record<string, string> = {};
    if (opts.include && opts.include.length) params.include = opts.include.join(',');
    if (opts.includeArchived) params.include_archived = 'true';
    if (opts.stand) params.stand = opts.stand;
    const response = await connectorApi.get<WorkflowSnapshot>(
      `/connector/integrations/control/${encodeURIComponent(integrationId)}/workflow`,
      {
        params,
        headers: opts.etag ? { 'If-None-Match': opts.etag } : undefined,
        validateStatus: (status) => status === 200 || status === 304,
      }
    );
    if (response.status === 304) return { status: 304 };
    const headers = (response.headers || {}) as Record<string, unknown>;
    const rawEtag = headers.etag ?? headers.ETag;
    const digest = response.data?.revision?.digest;
    // The header may not be exposed through CORS — it always equals the quoted digest.
    const etag = typeof rawEtag === 'string' && rawEtag ? rawEtag : digest ? `"${digest}"` : null;
    return { status: 200, snapshot: response.data, etag };
  },

  /**
   * POST …/workflow — dry run (plan with diff) or real apply, depending on
   * `body.dry_run`. Admin only. 409 when `base_revision` is stale.
   */
  saveWorkflow: async (integrationId: string, body: WorkflowSaveRequest): Promise<WorkflowSaveResponse> => {
    const response = await connectorApi.post<WorkflowSaveResponse>(
      `/connector/integrations/control/${encodeURIComponent(integrationId)}/workflow`,
      body
    );
    return response.data;
  },

  /** POST …/workflow/rollback — roll back to a `version_id` from `workflow_versions`. Admin only. */
  rollbackWorkflow: async (integrationId: string, body: WorkflowRollbackRequest): Promise<WorkflowRollbackResult> => {
    const response = await connectorApi.post<WorkflowRollbackResult>(
      `/connector/integrations/control/${encodeURIComponent(integrationId)}/workflow/rollback`,
      body
    );
    return response.data;
  },

  // User/control: add integration
  addIntegration: async (data: { url: string; apiKey?: string }): Promise<any> => {
    const response = await connectorApi.post('/connector/integrations/control', data);
    return response.data;
  },

  // User/control: list my integrations
  listMyIntegrations: async (): Promise<ConnectorIntegrationListResponse> => {
    const response = await connectorApi.get<ConnectorIntegrationListResponse>('/connector/integrations/control');
    return response.data;
  },

  // User/control: get integration
  getIntegration: async (integrationId: string): Promise<any> => {
    const response = await connectorApi.get(`/connector/integrations/control/${integrationId}`);
    return response.data;
  },

  // User/control: patch integration
  patchIntegration: async (integrationId: string, data: Record<string, any>): Promise<any> => {
    const response = await connectorApi.patch(`/connector/integrations/control/${integrationId}`, data);
    return response.data;
  },

  // User/control: delete integration
  deleteIntegration: async (integrationId: string): Promise<any> => {
    const response = await connectorApi.delete(`/connector/integrations/control/${integrationId}`);
    return response.data;
  },

  // Get datasets for integration
  getDatasets: async (integrationId: string): Promise<ApiResponse<import('../types/api').ConnectorDataset[]>> => {
    const response = await connectorApi.get<ApiResponse<import('../types/api').ConnectorDataset[]>>('/connector/datasets', { params: { integration_id: integrationId } });
    return response.data;
  },

  // Unified dataset content viewer endpoint for all dataset sources/types.
  getDatasetContent: async (datasetId: string, integrationId: string): Promise<ApiResponse<DatasetContentResponse>> => {
    const response = await api.get<ApiResponse<DatasetContentResponse>>(
      `/training/datasets/${encodeURIComponent(datasetId)}/content`,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  /**
   * Internal contour — service-token gated (AIT-121), NOT reachable with a user session.
   * Everything below is behind `REACT_APP_WORKFLOW_TEST_CHAT`, which exists only in
   * `.env.dev`: the key falls back to a placeholder, and shipping a service token in a
   * browser bundle would hand the whole internal contour to anyone with devtools.
   *
   * Passing `state` + `routing` switches the product to a full production turn on that
   * position instead of a shortened one (AIT-126) — that is what makes a before/after
   * run on an unapplied workflow possible at all.
   */
  replyDialogWithConfig: async (
    integrationId: string,
    body: {
      config: string;
      dialog: Array<{ type: 'human' | 'ai' | 'system'; content: string }>;
      /** Seat identity. Required together with `state`/`routing`. */
      user_id?: string;
      /** State definition to run the turn on: prompts, agent roster, triggers, map_config. */
      state?: unknown;
      /** Must travel with `state` — the pair is what the actor validates. */
      routing?: unknown;
      rag_enabled?: boolean;
      ltm_enabled?: boolean;
      diary_enabled?: boolean;
      portrait_enabled?: boolean;
      chat_id?: string;
    }
  ): Promise<any> => {
    const response = await connectorApi.post(
      `/connector/internal/integrations/${integrationId}/reply-dialog-with-config`,
      body,
      { headers: { 'X-Api-Key': internalServiceKey() } }
    );
    return response.data;
  },

  /**
   * A clean identity plus the `state`/`routing` it starts on. Isolation is per seat, not
   * per chat_id: two candidates on one identity would differ by launch order rather than
   * by config, because the Map, LTM, diary and portrait are written for real (AIT-126).
   */
  takeEphemeralSeat: async (integrationId: string): Promise<any> => {
    const response = await connectorApi.post(
      `/connector/internal/integrations/${integrationId}/ephemeral/seats`,
      {},
      { headers: { 'X-Api-Key': internalServiceKey() } }
    );
    return response.data;
  },

  releaseEphemeralSeat: async (integrationId: string, seatId: string): Promise<void> => {
    await connectorApi.delete(
      `/connector/internal/integrations/${integrationId}/ephemeral/seats/${encodeURIComponent(seatId)}`,
      { headers: { 'X-Api-Key': internalServiceKey() } }
    );
  },

  // Training jobs
  createTrainingJob: async (
    data: import('../types/api').TrainingJobRequestBody,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<any>> => {
    console.log('📤 Creating training job with payload:', JSON.stringify(data, null, 2));
    // Runtime axios supports AbortSignal; legacy @types/axios may omit `signal` on config — use cast.
    const response = await api.post<ApiResponse<any>>(
      '/training/jobs',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    console.log('✅ Training job created successfully:', response.data);
    return response.data;
  },

  // Preview before creating training job
  previewTrainingJob: async (
    data: import('../types/api').TrainingJobRequestBody,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      '/training/jobs/preview',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  /** Agents found in saved datasets (continuous / historical config training). */
  previewDatasetAgents: async (
    data: import('../types/api').DatasetAgentsPreviewRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<import('../types/api').DatasetAgentsPreviewResponse>> => {
    const response = await api.post<ApiResponse<import('../types/api').DatasetAgentsPreviewResponse>>(
      '/training/datasets/agents/preview',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  // Poll training job status (integration_id required in query per API)
  pollTrainingJob: async (jobId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/training/jobs/${jobId}/poll`, {}, { params: { integration_id: integrationId } });
    return response.data;
  },

  cancelTrainingJob: async (jobId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      `/training/jobs/${encodeURIComponent(jobId)}/cancel`,
      {},
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  /** Creates a new job from a failed/cancelled/partially_succeeded job; returns the new job payload. */
  retryTrainingJob: async (
    jobId: string,
    integrationId: string,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(
      `/training/jobs/${encodeURIComponent(jobId)}/retry`,
      {},
      {
        params: { integration_id: integrationId },
        timeout: 90_000,
        ...(options?.signal ? { signal: options.signal } : {}),
      } as any
    );
    return response.data;
  },

  // Get training job by ID (integration_id required in query)
  getTrainingJob: async (jobId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/training/jobs/${jobId}`, { params: { integration_id: integrationId } });
    return response.data;
  },

  // List all training jobs (integration_id required)
  listTrainingJobs: async (params: {
    integration_id: string;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{
    items: any[];
    pagination: { limit: number; offset: number; total: number };
  }>> => {
    const response = await api.get<ApiResponse<{
      items: any[];
      pagination: { limit: number; offset: number; total: number };
    }>>('/training/jobs', { params });
    return response.data;
  },

  // List fine-tuning history rows (new endpoint)
  listTrainingHistory: async (params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{
    items: any[];
    pagination: { limit: number; offset: number; total: number };
  }>> => {
    const response = await api.get<ApiResponse<{
      items: any[];
      pagination: { limit: number; offset: number; total: number };
    }>>('/training/history', { params });
    return response.data;
  },

  // List agents for an integration
  listIntegrationAgents: async (integrationId: string): Promise<ApiResponse<unknown>> => {
    const response = await api.get<ApiResponse<unknown>>(`/training/integrations/${integrationId}/agents`);
    return response.data;
  },

  /** DELETE /training/agents/{agent_id}/model — removes fine-tuned model linked to training agent */
  deleteTrainingAgentModel: async (agentId: string): Promise<ApiResponse<unknown>> => {
    const response = await api.delete<ApiResponse<unknown>>(
      `/training/agents/${encodeURIComponent(agentId)}/model`
    );
    return response.data;
  },

  // Auto Fine-Tuning configs
  createAutoConfig: async (data: {
    integration_id: string;
    enabled: boolean;
    schedule: string;
    regime: string;
    regime_percent: number;
    period_days: number;
    history_depth?: number;
    model_name_prefix: string;
    agents: Array<{
      agent_id: string;
      agent_name: string;
      dataset_id?: string;
      base_model?: string;
      is_trainable: boolean;
      filters?: { reactions?: string[]; comment?: boolean; groundtruth?: boolean };
    }>;
  }): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>('/training/auto/configs', data);
    return response.data;
  },

  listAutoConfigs: async (params: { integration_id: string }): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>('/training/auto/configs', { params });
    return response.data;
  },

  getAutoConfig: async (configId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/training/auto/configs/${configId}`, { params: { integration_id: integrationId } });
    return response.data;
  },

  updateAutoConfig: async (configId: string, integrationId: string, data: { enabled: boolean }): Promise<ApiResponse<any>> => {
    const response = await api.patch<ApiResponse<any>>(`/training/auto/configs/${configId}`, data, { params: { integration_id: integrationId } });
    return response.data;
  },

  /** Full update of auto config (PUT) */
  putAutoConfig: async (
    configId: string,
    integrationId: string,
    data: {
      enabled: boolean;
      schedule: string;
      regime: string;
      regime_percent: number;
      period_days: number;
      history_depth?: number;
      model_name_prefix: string;
      agents: Array<{
        agent_id: string;
        agent_name: string;
        dataset_id?: string;
        is_trainable: boolean;
        base_model?: string;
        filters?: { reactions?: string[]; comment?: boolean; groundtruth?: boolean };
      }>;
    }
  ): Promise<ApiResponse<any>> => {
    const response = await api.put<ApiResponse<any>>(`/training/auto/configs/${configId}`, data, { params: { integration_id: integrationId } });
    return response.data;
  },

  deleteAutoConfig: async (configId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.delete<ApiResponse<any>>(`/training/auto/configs/${configId}`, { params: { integration_id: integrationId } });
    return response.data;
  },

  getAutoConfigStatus: async (configId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>(`/training/auto/configs/${configId}/status`, { params: { integration_id: integrationId } });
    return response.data;
  },

  runAutoConfig: async (configId: string, integrationId: string): Promise<ApiResponse<any>> => {
    const response = await api.post<ApiResponse<any>>(`/training/auto/configs/${configId}/run`, {}, { params: { integration_id: integrationId } });
    return response.data;
  },

  // Get model lineage graph
  getModelLineage: async (params: {
    integration_id: string;
    view?: string;
    compact?: boolean;
    latest_per_alias?: boolean;
    limit_nodes?: number;
    limit_edges?: number;
  }): Promise<ApiResponse<any>> => {
    const response = await api.get<ApiResponse<any>>('/training/models/lineage', { params });
    return response.data;
  },

  // List trainable models for an integration (for Profile → Models tab and configurator)
  listTrainableModels: async (params: {
    integration_id: string;
    agent_id?: string;
    agent_name?: string;
    status?: string;
    succeeded_only?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<{ items: import('../types/api').TrainableModelItem[]; pagination?: { limit: number; offset: number; total: number } }>> => {
    const response = await api.get<ApiResponse<any>>('/training/models/trainable', { params });
    return response.data;
  },

  /** Export dataset for training (period + filters). Returns dataset_meta (id, label, inner_label, s3_key), train/validation. */
  exportDataset: async (
    data: DatasetExportRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<DatasetExportResponse>> => {
    const response = await api.post<ApiResponse<DatasetExportResponse>>(
      '/training/datasets/export',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  /** Start async dataset import job (same body as export). */
  createDatasetImportJob: async (
    data: DatasetExportRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<DatasetImportJob>> => {
    const response = await api.post<ApiResponse<DatasetImportJob>>(
      '/training/datasets/import-jobs',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  /** Poll async dataset import job status. */
  getDatasetImportJob: async (jobId: string): Promise<ApiResponse<DatasetImportJob>> => {
    const response = await api.get<ApiResponse<DatasetImportJob>>(`/training/datasets/import-jobs/${jobId}`);
    return response.data;
  },

  /** Cancel a queued or running dataset import job. */
  cancelDatasetImportJob: async (jobId: string): Promise<ApiResponse<DatasetImportJob>> => {
    const response = await api.post<ApiResponse<DatasetImportJob>>(
      `/training/datasets/import-jobs/${encodeURIComponent(jobId)}/cancel`
    );
    return response.data;
  },

  /** Preview export: token totals (POST /training/datasets/export/preview). */
  exportDatasetPreview: async (
    data: DatasetExportRequest,
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<DatasetExportPreviewData>> => {
    const response = await api.post<ApiResponse<DatasetExportPreviewData>>(
      '/training/datasets/export/preview',
      data,
      options?.signal ? ({ signal: options.signal } as any) : undefined
    );
    return response.data;
  },

  /** Consented Virtual Psychologist users for import filters (GET /training/datasets/source-users). */
  listDatasetSourceUsers: async (
    integrationId: string,
    opts?: { from?: string; to?: string }
  ): Promise<ApiResponse<DatasetSourceUsersData>> => {
    const response = await api.get<ApiResponse<DatasetSourceUsersData>>('/training/datasets/source-users', {
      params: {
        integration_id: integrationId,
        ...(opts?.from ? { from: opts.from } : {}),
        ...(opts?.to ? { to: opts.to } : {}),
      },
    });
    return response.data;
  },

  /** Source dialogs for import filters (GET /training/datasets/source-dialogs). */
  listDatasetSourceDialogs: async (
    integrationId: string,
    opts: {
      page: number;
      limit?: number;
      user_ids?: string[];
      dialog_ids?: string[];
      from?: string;
      to?: string;
    }
  ): Promise<ApiResponse<DatasetSourceDialogsData>> => {
    const params: Record<string, string | number> = {
      integration_id: integrationId,
      page: opts.page,
      limit: opts.limit ?? 50,
    };
    if (opts.from) params.from = opts.from;
    if (opts.to) params.to = opts.to;
    if (opts.user_ids && opts.user_ids.length > 0) params.user_ids = opts.user_ids.join(',');
    if (opts.dialog_ids && opts.dialog_ids.length > 0) params.dialog_ids = opts.dialog_ids.join(',');
    const response = await api.get<ApiResponse<DatasetSourceDialogsData>>('/training/datasets/source-dialogs', {
      params,
    });
    return response.data;
  },
};

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() ?? null;
  return null;
}

function withIntegration(integrationId: string, params?: Record<string, any>) {
  return { ...(params || {}), integration_id: integrationId };
}

function withAccessTokenAuth(headers?: Record<string, string>): Record<string, string> {
  const accessToken = getCookie('access_token');
  if (!accessToken) return headers ?? {};
  return { ...(headers ?? {}), Authorization: `Bearer ${accessToken}` };
}

// Virtual User Service endpoints (new)
export const vuser = {
  health: async (): Promise<{ status: string }> => {
    const response = await vuserApi.get<{ status: string }>('/vuser/health');
    return response.data;
  },

  // Virtual users
  createVirtualUser: async (
    integrationId: string,
    body: Pick<VirtualUser, 'type' | 'name' | 'description' | 'user_config'> & {
      ai_config?: string;
      virtual_user_system_prompt?: string | null;
      first_user_message_instruction?: string | null;
      followup_user_message_instruction?: string | null;
      memory_enabled?: boolean;
      diary_enabled?: boolean;
      portrait_enabled?: boolean;
    }
  ): Promise<ApiResponse<{ id: string; type: VirtualUser['type']; name: string }>> => {
    const response = await vuserApi.post<ApiResponse>('/vuser/virtual-users', body, { params: withIntegration(integrationId) });
    return response.data;
  },

  listVirtualUsers: async (integrationId: string): Promise<ApiResponse<VirtualUser[]>> => {
    const response = await vuserApi.get<ApiResponse<VirtualUser[]>>('/vuser/virtual-users', { params: withIntegration(integrationId) });
    return response.data;
  },

  getVirtualUser: async (integrationId: string, id: string): Promise<ApiResponse<VirtualUser>> => {
    const response = await vuserApi.get<ApiResponse<VirtualUser>>(`/vuser/virtual-users/${id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  updateVirtualUser: async (
    integrationId: string,
    id: string,
    body: Partial<
      Pick<
        VirtualUser,
        | 'name'
        | 'description'
        | 'user_config'
        | 'ai_config'
        | 'virtual_user_system_prompt'
        | 'first_user_message_instruction'
        | 'followup_user_message_instruction'
        | 'memory_enabled'
        | 'diary_enabled'
        | 'portrait_enabled'
      >
    >
  ): Promise<ApiResponse<VirtualUser>> => {
    const response = await vuserApi.put<ApiResponse<VirtualUser>>(`/vuser/virtual-users/${id}`, body, { params: withIntegration(integrationId) });
    return response.data;
  },

  deleteVirtualUser: async (integrationId: string, id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.delete<ApiResponse<{ message: string }>>(`/vuser/virtual-users/${id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  setPersona: async (integrationId: string, id: string, persona_id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.put<ApiResponse<{ message: string }>>(
      `/vuser/virtual-users/${id}/persona`,
      { persona_id },
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  // Personas
  createPersona: async (integrationId: string, body: Omit<Persona, 'id' | 'integration_id' | 'created_at' | 'updated_at'>): Promise<ApiResponse<Persona>> => {
    const response = await vuserApi.post<ApiResponse<Persona>>('/vuser/personas', body, { params: withIntegration(integrationId) });
    return response.data;
  },

  listPersonas: async (integrationId: string, virtual_user_id: string): Promise<ApiResponse<Persona[]>> => {
    const response = await vuserApi.get<ApiResponse<Persona[]>>('/vuser/personas', { params: withIntegration(integrationId, { virtual_user_id }) });
    return response.data;
  },

  getPersona: async (integrationId: string, id: string): Promise<ApiResponse<Persona>> => {
    const response = await vuserApi.get<ApiResponse<Persona>>(`/vuser/personas/${id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  updatePersona: async (
    integrationId: string,
    id: string,
    body: Partial<Pick<Persona, 'name' | 'description' | 'reaction_rules' | 'judge_meta_prompts'>>
  ): Promise<ApiResponse<Persona>> => {
    const response = await vuserApi.put<ApiResponse<Persona>>(`/vuser/personas/${id}`, body, { params: withIntegration(integrationId) });
    return response.data;
  },

  deletePersona: async (integrationId: string, id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.delete<ApiResponse<{ message: string }>>(`/vuser/personas/${id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  // Dialogs
  generateDialog: async (
    integrationId: string,
    body: {
      virtual_user_id: string;
      persona_id: string;
      title: string;
      initial_message: string;
      iterations?: number;
      config?: string;
    }
  ): Promise<ApiResponse<Dialog>> => {
    const response = await vuserApi.post<ApiResponse<Dialog>>('/vuser/dialogs/generate', body, { params: withIntegration(integrationId) });
    return response.data;
  },

  listDialogs: async (integrationId: string, virtual_user_id?: string): Promise<ApiResponse<Dialog[]>> => {
    const response = await vuserApi.get<ApiResponse<Dialog[]>>('/vuser/dialogs', { params: withIntegration(integrationId, virtual_user_id ? { virtual_user_id } : undefined) });
    return response.data;
  },

  getDialog: async (integrationId: string, id: string): Promise<ApiResponse<Dialog>> => {
    const response = await vuserApi.get<ApiResponse<Dialog>>(`/vuser/dialogs/${id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  getDialogStatistics: async (integrationId: string, id: string): Promise<ApiResponse<DialogTokenStatistics>> => {
    const response = await vuserApi.get<ApiResponse<DialogTokenStatistics>>(`/vuser/dialogs/${id}/statistics`, { params: withIntegration(integrationId) });
    return response.data;
  },

  deleteDialog: async (integrationId: string, id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.delete<ApiResponse<{ message: string }>>(`/vuser/dialogs/${id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  // Reactions
  reactionsByDialog: async (integrationId: string, dialog_id: string): Promise<ApiResponse<Reaction[]>> => {
    const response = await vuserApi.get<ApiResponse<Reaction[]>>(`/vuser/reactions/dialog/${dialog_id}`, { params: withIntegration(integrationId) });
    return response.data;
  },
  reactionsByMessage: async (integrationId: string, message_id: string): Promise<ApiResponse<Reaction[]>> => {
    const response = await vuserApi.get<ApiResponse<Reaction[]>>(`/vuser/reactions/message/${message_id}`, { params: withIntegration(integrationId) });
    return response.data;
  },
  reactionsByVirtualUser: async (integrationId: string, virtual_user_id: string): Promise<ApiResponse<Reaction[]>> => {
    const response = await vuserApi.get<ApiResponse<Reaction[]>>(`/vuser/reactions/virtual-user/${virtual_user_id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  /**
   * Стейты активного воркфлоу интеграции — список для выбора стартовой позиции
   * эксперимента. Отсортирован бэкендом, порядок стабилен между запросами.
   *
   * 404 «integration has no active workflow» — не ошибка интерфейса: на такой
   * интеграции выбирать не из чего. Обрабатывать отдельно от прочих сбоев.
   */
  getWorkflowStates: async (
    integrationId: string
  ): Promise<import('../types/vuser').WorkflowStatesSnapshot> => {
    const response = await api.get<ApiResponse<import('../types/vuser').WorkflowStatesSnapshot>>(
      `/training/integrations/${encodeURIComponent(integrationId)}/workflow-states`
    );
    const payload = response.data as ApiResponse<import('../types/vuser').WorkflowStatesSnapshot>;
    if (!payload?.success || !payload.data) {
      throw new Error(payload?.error?.message || 'Failed to load workflow states');
    }
    const data = payload.data;
    // Контракт обещает массив, но доверять этому нельзя: неполный ответ ронял
    // всю страницу на `states.length`. Нормализуем на границе, а не в разметке.
    return {
      ...data,
      states: Array.isArray(data.states)
        ? data.states.filter((state) => typeof state?.state_id === 'string').map((state) => ({
            state_id: state.state_id,
            coordinator_flags_required: Array.isArray(state.coordinator_flags_required)
              ? state.coordinator_flags_required
              : [],
          }))
        : [],
    };
  },

  // Experiments
  listExperiments: async (integrationId: string): Promise<ApiResponse<Experiment[]>> => {
    const response = await vuserApi.get<ApiResponse<Experiment[]>>('/vuser/experiments', { params: withIntegration(integrationId) });
    return response.data;
  },

  startExperiment: async (
    integrationId: string,
    body: Experiment['config'] & {
      experiment_name?: string;
      train_ratio?: number;
      validation_ratio?: number;
      limit_history?: boolean;
      history_depth?: number | null;
      reaction_weights?: import('../types/reactionWeights').ReactionWeights;
    }
  ): Promise<ApiResponse<Experiment>> => {
    const response = await vuserApi.post<ApiResponse<Experiment>>('/vuser/experiments/start', body, { params: withIntegration(integrationId) });
    return response.data;
  },

  getExperiment: async (integrationId: string, id: string): Promise<ApiResponse<Experiment>> => {
    const response = await vuserApi.get<ApiResponse<Experiment>>(`/vuser/experiments/${id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  cancelExperiment: async (integrationId: string, id: string): Promise<ApiResponse<{ status: string }>> => {
    const response = await vuserApi.post<ApiResponse<{ status: string }>>(`/vuser/experiments/${id}/cancel`, {}, { params: withIntegration(integrationId) });
    return response.data;
  },

  /** Creates a new experiment from a failed/cancelled one; returns the new experiment. */
  retryExperiment: async (experimentId: string, integrationId: string): Promise<ApiResponse<Experiment>> => {
    const response = await vuserApi.post<ApiResponse<Experiment>>(
      `/vuser/experiments/${encodeURIComponent(experimentId)}/retry`,
      {},
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  deleteExperiment: async (integrationId: string, id: string): Promise<ApiResponse<{ message: string }>> => {
    const response = await vuserApi.delete<ApiResponse<{ message: string }>>(`/vuser/experiments/${id}`, { params: withIntegration(integrationId) });
    return response.data;
  },

  scoreExperiment: async (integrationId: string, id: string): Promise<ApiResponse<ExperimentScoreBreakdown>> => {
    const response = await vuserApi.get<ApiResponse<ExperimentScoreBreakdown>>(`/vuser/experiments/${id}/score`, { params: withIntegration(integrationId) });
    return response.data;
  },

  experimentDialogs: async (integrationId: string, id: string): Promise<ApiResponse<Dialog[]>> => {
    const response = await vuserApi.get<ApiResponse<Dialog[]>>(`/vuser/experiments/${id}/dialogs`, { params: withIntegration(integrationId) });
    return response.data;
  },

  experimentStatistics: async (integrationId: string, id: string): Promise<ApiResponse<ExperimentTokenStatistics>> => {
    const response = await vuserApi.get<ApiResponse<ExperimentTokenStatistics>>(`/vuser/experiments/${id}/statistics`, { params: withIntegration(integrationId) });
    return response.data;
  },

  /**
   * Build train/validation dataset JSON from experiment dialogs and persist for the integration (training pipeline).
   * POST /vuser/experiments/dataset?integration_id=
   */
  uploadExperimentDataset: async (
    integrationId: string,
    body: {
      experiment_ids: string[];
      history_depth?: number;
      limit_history?: boolean;
      reaction_weights?: import('../types/reactionWeights').ReactionWeights;
    }
  ): Promise<ApiResponse<Record<string, unknown>>> => {
    const response = await vuserApi.post<ApiResponse<Record<string, unknown>>>(
      '/vuser/experiments/dataset',
      body,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  /** ML token usage prediction for dataset generation (direct response, no success/data wrapper). */
  predictDatasetGenerationTokenUsage: async (
    integrationId: string,
    body: import('../types/datasetGenerationPreview').DatasetGenerationPredictTokenUsageRequest
  ): Promise<import('../types/datasetGenerationPreview').DatasetGenerationPredictTokenUsageResponse> => {
    const response = await vuserApi.post<
      import('../types/datasetGenerationPreview').DatasetGenerationPredictTokenUsageResponse
    >('/vuser/dataset_generation_predict_token_usage', body, {
      params: withIntegration(integrationId),
      headers: withAccessTokenAuth(),
    });
    return response.data;
  },

  // Chat viewer — labels & resolved evaluation (message + step level)
  chatViewerGetMessageLabel: async (
    integrationId: string,
    messageId: string
  ): Promise<ApiResponse<ChatViewerLabelResponse>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerLabelResponse>>(
      `/vuser/chat-viewer/messages/${messageId}/label`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerPutMessageLabel: async (
    integrationId: string,
    messageId: string,
    body: ChatViewerLabelUpsertBody
  ): Promise<ApiResponse<unknown>> => {
    const response = await vuserApi.put<ApiResponse<unknown>>(
      `/vuser/chat-viewer/messages/${messageId}/label`,
      body,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetMessageResolvedLabel: async (
    integrationId: string,
    messageId: string
  ): Promise<ApiResponse<ChatViewerResolvedLabel>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerResolvedLabel>>(
      `/vuser/chat-viewer/messages/${messageId}/resolved-label`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetStepLabel: async (
    integrationId: string,
    stepId: string
  ): Promise<ApiResponse<ChatViewerLabelResponse>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerLabelResponse>>(
      `/vuser/chat-viewer/steps/${stepId}/label`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerPutStepLabel: async (
    integrationId: string,
    stepId: string,
    body: ChatViewerLabelUpsertBody
  ): Promise<ApiResponse<unknown>> => {
    const response = await vuserApi.put<ApiResponse<unknown>>(
      `/vuser/chat-viewer/steps/${stepId}/label`,
      body,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetStepResolvedLabel: async (
    integrationId: string,
    stepId: string
  ): Promise<ApiResponse<ChatViewerResolvedLabel>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerResolvedLabel>>(
      `/vuser/chat-viewer/steps/${stepId}/resolved-label`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetStepReactions: async (
    integrationId: string,
    stepId: string
  ): Promise<ApiResponse<ChatViewerStepVirtualReaction[]>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerStepVirtualReaction[]>>(
      `/vuser/chat-viewer/steps/${stepId}/reactions`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetMessageStepReactions: async (
    integrationId: string,
    messageId: string
  ): Promise<ApiResponse<ChatViewerStepVirtualReaction[]>> => {
    const response = await vuserApi.get<ApiResponse<ChatViewerStepVirtualReaction[]>>(
      `/vuser/chat-viewer/messages/${messageId}/step-reactions`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetMessageStepLabels: async (
    integrationId: string,
    messageId: string
  ): Promise<ApiResponse<unknown>> => {
    const response = await vuserApi.get<ApiResponse<unknown>>(
      `/vuser/chat-viewer/messages/${messageId}/step-labels`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  chatViewerGetDialogStepLabels: async (
    integrationId: string,
    dialogId: string
  ): Promise<ApiResponse<unknown>> => {
    const response = await vuserApi.get<ApiResponse<unknown>>(
      `/vuser/chat-viewer/dialogs/${dialogId}/step-labels`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  // RAG folders
  ragListFolders: async (integrationId: string): Promise<ApiResponse<RagFolder[]>> => {
    const response = await vuserApi.get<ApiResponse<RagFolder[]>>('/vuser/rag/folders', {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  ragCreateFolder: async (
    integrationId: string,
    body: { name: string; description?: string }
  ): Promise<ApiResponse<RagFolder>> => {
    const response = await vuserApi.post<ApiResponse<RagFolder>>('/vuser/rag/folders', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  ragUpdateFolder: async (
    integrationId: string,
    folderId: string,
    body: { name?: string; description?: string }
  ): Promise<ApiResponse<RagFolder>> => {
    const response = await vuserApi.put<ApiResponse<RagFolder>>(
      `/vuser/rag/folders/${encodeURIComponent(folderId)}`,
      body,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  ragDeleteFolder: async (
    integrationId: string,
    folderId: string
  ): Promise<ApiResponse<RagDeleteFolderResponse>> => {
    const response = await vuserApi.delete<ApiResponse<RagDeleteFolderResponse>>(
      `/vuser/rag/folders/${encodeURIComponent(folderId)}`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  // RAG documents (scoped by integration_url via integration_id)
  ragPresignDocument: async (
    integrationId: string,
    body: { filename: string; mime_type: string; folder_id?: string | null }
  ): Promise<ApiResponse<RagPresignResponseData>> => {
    const response = await vuserApi.post<ApiResponse<RagPresignResponseData>>(
      '/vuser/rag/documents/presign',
      body,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  /** PUT binary file to S3 presigned URL (not same-origin; uses fetch). Retries on transient transport failures. */
  ragUploadToPresignedUrl: async (uploadUrl: string, file: Blob, mimeType: string): Promise<void> => {
    const maxAttempts = 3;
    const backoffMs = [0, 450, 1200];
    let lastErr: unknown;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      if (backoffMs[attempt] > 0) {
        await new Promise((r) => setTimeout(r, backoffMs[attempt]));
      }
      try {
        const res = await fetch(uploadUrl, {
          method: 'PUT',
          headers: {
            'Content-Type': mimeType,
            'x-amz-acl': 'private',
          },
          body: file,
        });
        if (!res.ok) {
          const t = await res.text().catch(() => '');
          throw new Error(t || `Upload failed: ${res.status} ${res.statusText}`);
        }
        return;
      } catch (e) {
        lastErr = e;
        const canRetry = attempt < maxAttempts - 1 && isLikelyTransientFetchError(e);
        if (!canRetry) throw e;
      }
    }
    throw lastErr;
  },

  ragNotifyDocumentUploaded: async (integrationId: string, docId: string): Promise<ApiResponse<RagDocument>> => {
    const response = await vuserApi.post<ApiResponse<RagDocument>>(
      `/vuser/rag/documents/${encodeURIComponent(docId)}/uploaded`,
      {},
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  ragListDocuments: async (
    integrationId: string,
    opts?: { folder_id?: string | null }
  ): Promise<ApiResponse<RagDocument[]>> => {
    const params = withIntegration(
      integrationId,
      opts?.folder_id != null && opts.folder_id !== '' ? { folder_id: opts.folder_id } : undefined
    );
    const response = await vuserApi.get<ApiResponse<RagDocument[]>>('/vuser/rag/documents', { params });
    return response.data;
  },

  ragSetDocumentUsable: async (
    integrationId: string,
    body: { doc_id: string; usable: boolean }
  ): Promise<ApiResponse<RagDocument>> => {
    const response = await vuserApi.post<ApiResponse<RagDocument>>('/vuser/rag/documents/usable', body, {
      params: withIntegration(integrationId),
    });
    return response.data;
  },

  ragSetDocumentUsableByPath: async (
    integrationId: string,
    docId: string,
    usable: boolean
  ): Promise<ApiResponse<RagDocument>> => {
    const response = await vuserApi.post<ApiResponse<RagDocument>>(
      `/vuser/rag/documents/${encodeURIComponent(docId)}/usable`,
      { usable },
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  ragPatchDocumentFolder: async (
    integrationId: string,
    docId: string,
    folderId: string | null
  ): Promise<ApiResponse<RagDocument>> => {
    const response = await vuserApi.patch<ApiResponse<RagDocument>>(
      `/vuser/rag/documents/${encodeURIComponent(docId)}/folder`,
      { folder_id: folderId },
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },

  ragDeleteDocument: async (
    integrationId: string,
    documentId: string
  ): Promise<ApiResponse<RagDeleteResponseData>> => {
    const response = await vuserApi.delete<ApiResponse<RagDeleteResponseData>>(
      `/vuser/rag/documents/${encodeURIComponent(documentId)}`,
      { params: withIntegration(integrationId) }
    );
    return response.data;
  },
};

// ─── NLT (Natural Language Tuner) endpoints ───────────────────────────────────

export const nlt = {
  // ── Admin: NLT Configs ────────────────────────────────────────────────────

  listNLTConfigs: async (params?: {
    limit?: number;
    offset?: number;
    q?: string;
  }): Promise<ApiResponse<NLTPaginated<NLTConfig>>> => {
    const response = await api.get<ApiResponse<NLTPaginated<NLTConfig>>>('/admin/nlt/configs', { params });
    return response.data;
  },

  getLatestNLTConfig: async (): Promise<ApiResponse<NLTConfig>> => {
    const response = await api.get<ApiResponse<NLTConfig>>('/admin/nlt/configs/latest');
    return response.data;
  },

  getNLTConfig: async (configId: string): Promise<ApiResponse<NLTConfig>> => {
    const response = await api.get<ApiResponse<NLTConfig>>(`/admin/nlt/configs/${configId}`);
    return response.data;
  },

  saveNLTConfig: async (body: SaveNLTConfigRequest): Promise<ApiResponse<NLTConfig>> => {
    const response = await api.post<ApiResponse<NLTConfig>>('/admin/nlt/configs/save', body);
    return response.data;
  },

  deleteNLTConfig: async (configId: string): Promise<ApiResponse<unknown>> => {
    const response = await api.delete<ApiResponse<unknown>>(`/admin/nlt/configs/${configId}`);
    return response.data;
  },

  // ── User: Patch Configs ───────────────────────────────────────────────────

  savePatchConfig: async (
    integrationId: string,
    body: SavePatchConfigRequest
  ): Promise<ApiResponse<SavePatchConfigResponse>> => {
    const response = await api.post<ApiResponse<SavePatchConfigResponse>>(
      '/nlt/configs',
      body,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  listPatchConfigs: async (
    integrationId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<NLTPaginated<NLTPatchConfig>>> => {
    const response = await api.get<ApiResponse<NLTPaginated<NLTPatchConfig>>>(
      '/nlt/configs',
      { params: { integration_id: integrationId, ...params } }
    );
    return response.data;
  },

  getPatchConfig: async (
    configId: string,
    integrationId: string
  ): Promise<ApiResponse<NLTPatchConfig>> => {
    const response = await api.get<ApiResponse<NLTPatchConfig>>(
      `/nlt/configs/${configId}`,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  listConfigVersions: async (
    configId: string,
    integrationId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<ApiResponse<NLTPaginated<NLTPatchVersion>>> => {
    const response = await api.get<ApiResponse<NLTPaginated<NLTPatchVersion>>>(
      `/nlt/configs/${configId}/versions`,
      { params: { integration_id: integrationId, ...params } }
    );
    return response.data;
  },

  // ── User: Generate Patch ──────────────────────────────────────────────────

  generatePatch: async (
    integrationId: string,
    body: GeneratePatchRequest
  ): Promise<ApiResponse<NLTPatchRun>> => {
    const response = await api.post<ApiResponse<NLTPatchRun>>(
      '/nlt/patch',
      body,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  /**
   * POST /nlt/workflow-patch — the workflow twin of `generatePatch`. Same scope
   * (user + integration_id); the backend proposes and never writes, so applying the
   * result goes through `connector.saveWorkflow`.
   *
   * The published contract shows the response body unwrapped while every other `/nlt/*`
   * route answers in the `{success, data}` envelope, and this path has not been called
   * live yet — so accept either rather than breaking on whichever it turns out to be.
   */
  generateWorkflowPatch: async (
    integrationId: string,
    body: NltWorkflowPatchRequest
  ): Promise<NltWorkflowPatchResponse> => {
    const response = await api.post<NltWorkflowPatchResponse | ApiResponse<NltWorkflowPatchResponse>>(
      '/nlt/workflow-patch',
      body,
      { params: { integration_id: integrationId } }
    );
    const payload = response.data as ApiResponse<NltWorkflowPatchResponse>;
    if (payload && typeof payload === 'object' && 'success' in payload) {
      if (!payload.success || !payload.data) {
        throw new Error(payload.error?.message || 'Failed to generate a workflow patch');
      }
      return payload.data;
    }
    return response.data as NltWorkflowPatchResponse;
  },

  chats: async (
    integrationId: string,
    body: NLTChatRequest,
    options?: { chatType?: NLTChatType }
  ): Promise<ApiResponse<NLTChatResponse>> => {
    const response = await api.post<ApiResponse<NLTChatResponse>>(
      '/nlt/chats',
      body,
      {
        params: {
          integration_id: integrationId,
          ...(options?.chatType ? { chat_type: options.chatType } : {}),
        },
      }
    );
    return response.data;
  },

  // ── User: Accept / Reject Patch ───────────────────────────────────────────

  decidePatch: async (
    patchId: string,
    integrationId: string,
    body: PatchDecisionRequest
  ): Promise<ApiResponse<PatchDecisionAcceptResponse | PatchDecisionRejectResponse>> => {
    const response = await api.post<ApiResponse<PatchDecisionAcceptResponse | PatchDecisionRejectResponse>>(
      `/nlt/patches/${patchId}/decision`,
      body,
      { params: { integration_id: integrationId } }
    );
    return response.data;
  },

  // ── User: History ─────────────────────────────────────────────────────────

  getHistory: async (
    integrationId: string,
    params?: { config_id?: string; limit?: number; offset?: number }
  ): Promise<ApiResponse<NLTPaginated<NLTPatchRun>>> => {
    const response = await api.get<ApiResponse<NLTPaginated<NLTPatchRun>>>(
      '/nlt/history',
      { params: { integration_id: integrationId, ...params } }
    );
    return response.data;
  },
};

export default api; 
